"use client";

import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Popover } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Calendar, ChevronDown } from "lucide-react";
type DatesRangeValue = [Date | null, Date | null];

function parseYmdLocal(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0);
}

function dateToYmdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatRangeSummary(from?: string, to?: string): string {
  if (!from && !to) return "All dates";
  const opt: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  if (from && to) {
    const a = parseYmdLocal(from).toLocaleDateString(undefined, opt);
    const b = parseYmdLocal(to).toLocaleDateString(undefined, opt);
    return `${a} → ${b}`;
  }
  if (from) return `${parseYmdLocal(from).toLocaleDateString(undefined, opt)} → …`;
  if (to) return `… → ${parseYmdLocal(to).toLocaleDateString(undefined, opt)}`;
  return "All dates";
}

function filtersToDraft(from?: string, to?: string): DatesRangeValue {
  return [
    from ? parseYmdLocal(from) : null,
    to ? parseYmdLocal(to) : null,
  ];
}

function buildRangePresets(): { value: [Date, Date]; label: string }[] {
  const today = dayjs();
  const end = today.endOf("day").toDate();
  return [
    {
      value: [today.subtract(6, "day").startOf("day").toDate(), end],
      label: "Last 7 days",
    },
    {
      value: [today.subtract(29, "day").startOf("day").toDate(), end],
      label: "Last 30 days",
    },
    {
      value: [today.startOf("month").toDate(), end],
      label: "This month",
    },
    {
      value: [
        today.subtract(1, "month").startOf("month").toDate(),
        today.subtract(1, "month").endOf("month").toDate(),
      ],
      label: "Last month",
    },
    {
      value: [today.startOf("year").toDate(), end],
      label: "Year to date",
    },
  ];
}

interface TransactionDateRangePickerProps {
  dateFrom?: string;
  dateTo?: string;
  commitDateRange: (dateFrom?: string, dateTo?: string) => void;
  /** Shown above preset buttons. */
  rangeHint?: string;
  /** Short accessibility label for the trigger (prepended before summary). */
  ariaRangeKind?: string;
}

export function TransactionDateRangePicker({
  dateFrom,
  dateTo,
  commitDateRange,
  rangeHint = "Show transactions between",
  ariaRangeKind = "Transaction date range",
}: TransactionDateRangePickerProps) {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<DatesRangeValue>([null, null]);

  const summary = useMemo(
    () => formatRangeSummary(dateFrom, dateTo),
    [dateFrom, dateTo],
  );

  function commit(from: string | undefined, to: string | undefined) {
    commitDateRange(from, to);
  }

  function handleOpenChange(next: boolean) {
    setOpened(next);
    if (next) {
      setDraft(filtersToDraft(dateFrom, dateTo));
    }
  }

  function handlePickerChange(next: DatesRangeValue) {
    setDraft(next);
    const [from, to] = next;
    if (from && to) {
      commit(dateToYmdLocal(from), dateToYmdLocal(to));
      return;
    }
    if (!from && !to) {
      commit(undefined, undefined);
    }
  }

  function handleClear() {
    setDraft([null, null]);
    commit(undefined, undefined);
    setOpened(false);
  }

  const defaultPickerMonth = dateFrom
    ? parseYmdLocal(dateFrom)
    : dateTo
      ? parseYmdLocal(dateTo)
      : new Date();

  return (
    <Popover
      opened={opened}
      onChange={handleOpenChange}
      position="bottom-end"
      shadow="md"
      radius="md"
      withinPortal
    >
      <Popover.Target>
        <button
          type="button"
          title={summary}
          aria-expanded={opened}
          aria-haspopup="dialog"
          aria-label={`${ariaRangeKind}. ${summary}. Open calendar.`}
          className="inline-flex h-9 max-w-[min(100vw-3rem,320px)] items-center gap-2 rounded-md border border-[#d9e1ea] bg-[#f4f7fb] px-2.5 text-left text-sm font-medium text-[#2d3f57] outline-none ring-blue-500 hover:bg-[#eef3f9] focus-visible:ring-2"
          onClick={() => handleOpenChange(!opened)}
        >
          <Calendar className="h-4 w-4 shrink-0 text-[#6b7f99]" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{summary}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-[#6b7f99] transition-transform ${opened ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
      </Popover.Target>
      <Popover.Dropdown className="border border-[#e5ebf2] p-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#6b7f99]">
          {rangeHint}
        </p>
        <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Quick ranges">
          {buildRangePresets().map((p) => (
            <button
              key={p.label}
              type="button"
              className="rounded-md border border-[#d9e1ea] bg-[#f8fafc] px-2.5 py-1.5 text-xs font-medium text-[#2d3f57] hover:bg-[#eef3f9]"
              onClick={() => {
                const [a, b] = p.value;
                setDraft([a, b]);
                commit(dateToYmdLocal(a), dateToYmdLocal(b));
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <DatePicker
          type="range"
          allowSingleDateInRange
          weekendDays={[]}
          value={draft}
          onChange={handlePickerChange}
          defaultDate={defaultPickerMonth}
          size="sm"
        />
        <div className="mt-3 flex justify-end border-t border-[#e5ebf2] pt-3">
          <button
            type="button"
            className="text-sm font-medium text-[#52667f] underline-offset-2 hover:text-[#2d3f57] hover:underline"
            onClick={handleClear}
          >
            Clear filter (all dates)
          </button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
